import {
  InstallableContainer,
  Theme,
  ToastProvider,
} from "@vestaboard/installables";
import { PostHogProvider } from "posthog-js/react";
import { TabsArea } from "./components/TabsArea";
import {
  ENVIRONMENT,
  POSTHOG_CLIENT_KEY,
  POSTHOG_PROXY_ENDPOINT,
} from "./constants";
import { useGetQueryString } from "./hooks/useGetQueryString";
import { useEffect } from "react";
import posthog from "posthog-js";

function personIdFromQuery(): string {
  if (typeof window === "undefined") {
    return "";
  }
  return new URLSearchParams(window.location.search).get("person_id") || "";
}

function subscriptionIdFromQuery(): string {
  if (typeof window === "undefined") {
    return "";
  }
  return (
    new URLSearchParams(window.location.search).get("subscription_id") || ""
  );
}

const SERVICE_NAME = "blended-installable-frontend";

// "production" covers both "production" and "prod"; every other value
// (staging, dev, empty) is reported as "staging".
const NORMALIZED_ENVIRONMENT =
  ENVIRONMENT === "production" || ENVIRONMENT === "prod"
    ? "production"
    : "staging";

let posthogInitialized = false;

function initPostHog(): void {
  if (posthogInitialized || typeof window === "undefined") {
    return;
  }
  if (!POSTHOG_CLIENT_KEY || !POSTHOG_PROXY_ENDPOINT) {
    return;
  }
  posthogInitialized = true;

  const personId = personIdFromQuery();
  const subscriptionId = subscriptionIdFromQuery();
  posthog.init(POSTHOG_CLIENT_KEY, {
    api_host: POSTHOG_PROXY_ENDPOINT,
    autocapture: false,
    capture_pageview: false,
    capture_exceptions: true,
    // First /flags request must use person_id. PostHogProvider's useEffect
    // init runs after child identify() and leaves an anonymous distinct_id.
    bootstrap: personId
      ? { distinctID: personId, isIdentifiedID: true }
      : undefined,
    loaded: (client) => {
      if (personId && client.get_distinct_id() !== personId) {
        client.identify(personId);
      }
      // Tags every event (including autocaptured exceptions) with the
      // service/environment and, when known at load time, personId/
      // subscriptionId, so errors are filterable by those fields in
      // PostHog's Error Tracking UI — distinct_id alone only drives the
      // identity column, not a filterable event property. This installable
      // is scoped to a subscription/channel rather than a single board, so
      // subscriptionId is the available context (there is no board_id query
      // param). IdentifyContext keeps these two in sync post-mount, since
      // this callback only runs once at init.
      client.register({
        service: SERVICE_NAME,
        environment: NORMALIZED_ENVIRONMENT,
        ...(personId ? { personId } : {}),
        ...(subscriptionId ? { subscriptionId } : {}),
      });
    },
  });
}

initPostHog();

const IdentifyContext = () => {
  const { personId, subscriptionId } = useGetQueryString();

  useEffect(() => {
    if (!personId || posthog.get_distinct_id() === personId) {
      return;
    }
    posthog.identify(personId);
  }, [personId]);

  // Keep registered event properties in sync with the current URL, since
  // the init-time registration in initPostHog only runs once and would
  // otherwise leave stale/missing personId or subscriptionId on later
  // events (including exceptions) if either changes after mount.
  useEffect(() => {
    if (personId) {
      posthog.register({ personId });
    } else {
      posthog.unregister("personId");
    }
  }, [personId]);

  useEffect(() => {
    if (subscriptionId) {
      posthog.register({ subscriptionId });
    } else {
      posthog.unregister("subscriptionId");
    }
  }, [subscriptionId]);

  return null;
};

export const App = () => (
  <Theme>
    <PostHogProvider client={posthog}>
      <IdentifyContext />
      <ToastProvider offset={65}>
        <InstallableContainer>
          <TabsArea />
        </InstallableContainer>
      </ToastProvider>
    </PostHogProvider>
  </Theme>
);
